import React, { useMemo } from "react";
import { css, Theme, useTheme } from "@emotion/react";
import { isEqual } from "lodash";

import Image from "core/Components/Image";
import useBreakpoint from "core/hooks/useBreakpoint";
import ButtonsContainer from "elements/Components/TopBanner/components/ButtonsContainer";
import CommonButton from "core/Components/Buttons/CommonButton";

type Props = {
    topBannerImage: TopBannerImage,
    topBannerHeight: number,
    hasUniversalButton: boolean,
    showButton?: boolean,
};

const BannerImage: React.FC<Props> = ({ topBannerImage, topBannerHeight, hasUniversalButton, showButton = true }) => {
    const theme: Theme = useTheme();
    const { minWidth } = useBreakpoint();

    const [buttonHeight, buttonSidePadding] = useMemo(() => {
        switch (true) {
            case minWidth >= theme.breakpoints.sizes["3xl"]:
                return [54, 31];
            default:
                return [42, 22];
        }
    }, [minWidth, theme.breakpoints.sizes]);

    const image = useMemo(() => {
        if (minWidth >= theme.breakpoints.sizes["lg"]) {
            return topBannerImage.landscape || topBannerImage.portrait;
        }

        return topBannerImage.portrait || topBannerImage.landscape;
    }, [minWidth, topBannerImage, theme.breakpoints.sizes]);

    const useAFocusPoint = (isEqual(image, topBannerImage.landscape) && topBannerImage.useFocusPoint) ||
        (isEqual(image, topBannerImage.portrait) && topBannerImage.useFocusPointPortrait);

    const style = (theme: Theme) => css`
        display: flex;
        justify-content: center;
        width: 100vw;
        height: ${topBannerHeight}px;
        
        ${theme.breakpoints.down('sm')} {
            min-width: 360px;
        }
    `;

    const imageStyle = css`
         width: 100%;
         height: 100%;

        ${!useAFocusPoint && css`
            ${theme.breakpoints.down("md")} {
                > img {
                    display: flex;
                    min-height: unset !important;
                    max-height: unset !important;
                    height: unset !important;
                    width: 100% !important;
                    inset: unset !important;
                }
            }
        `}
    `;

    const altText = topBannerImage.altText || '';

    return (
        <div css={style} >
            <Image
                image={image}
                css={imageStyle}
                useFocusPoint={useAFocusPoint}
                customAltText={altText}
            />
            {!hasUniversalButton && (
                <ButtonsContainer showButton={showButton}>
                    {topBannerImage.linkButtons.map((button: LinkButton, index: number) => (
                        <CommonButton
                            key={`link-button-${index}`}
                            buttonData={button}
                            buttonHeight={buttonHeight}
                            buttonSidePadding={buttonSidePadding}
                        />
                    ))}
                </ButtonsContainer>
            )}
        </div>
    );
}

export default BannerImage;
